import { AlertCircle, CheckCircle2, Info, X, AlertTriangle } from "lucide-react";
import { useUIStore, type ToastLevel } from "@/stores/useUIStore";
import { cn } from "@/lib/cn";

const LEVEL_ICON: Record<ToastLevel, typeof Info> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  error: AlertCircle,
};

const LEVEL_CLASSES: Record<ToastLevel, string> = {
  info: "border-stroke-strong",
  success: "border-profit/50",
  warning: "border-amber/50",
  error: "border-loss/50",
};

const LEVEL_ICON_COLOR: Record<ToastLevel, string> = {
  info: "text-ink-muted",
  success: "text-profit",
  warning: "text-amber",
  error: "text-loss",
};

/**
 * Fixed toast stack in the bottom-right corner. Mount once near the root.
 */
export function ToastContainer() {
  const toasts = useUIStore((s) => s.toasts);
  const remove = useUIStore((s) => s.removeToast);

  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="pointer-events-none fixed right-6 bottom-6 z-50 flex flex-col gap-2"
    >
      {toasts.map((t) => {
        const Icon = LEVEL_ICON[t.level];
        return (
          <div
            key={t.id}
            role="status"
            className={cn(
              "pointer-events-auto flex max-w-sm items-start gap-3",
              "rounded-2xl border bg-surface px-4 py-3 pr-9",
              "shadow-[0_24px_48px_-20px_#000,inset_0_1px_0_rgba(255,255,255,0.04)]",
              "animate-in fade-in slide-in-from-bottom-2",
              LEVEL_CLASSES[t.level],
            )}
          >
            <Icon size={20} className={cn("shrink-0 mt-0.5", LEVEL_ICON_COLOR[t.level])} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-ink leading-snug">{t.message}</p>
              {t.description && (
                <p className="mt-0.5 text-xs text-ink-muted">{t.description}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => remove(t.id)}
              aria-label="Zavřít"
              className={cn(
                "absolute right-2 top-2 rounded-md p-1",
                "text-ink-dim hover:text-ink hover:bg-surface-raised",
              )}
            >
              <X size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
